/**
 * The logo editor (M6 plan P33, spec §7.1): the code kit's seeded drafts to pick from, then the player's finishing
 * steps (shape, palette, rotation, border, invert, undo). Every step is recorded in the recipe's `finishing`, which is
 * the `humanContribution` (D-026). Thin: the kit lives in `logoKit.ts`.
 */
import type { ReactNode } from 'react'
import type { LogoRecipe } from '../../strategic/types'
import { FocusButton } from '../focus'
import { applyStep, humanContribution, logoColors, logoKit, nextValue, pickDraft, resolveLogo, seededDrafts, shapePath, type LogoOp } from './logoKit'

/** A logo drawn from its recipe. */
export function LogoMark(p: { recipe: LogoRecipe; size: number; testId?: string }): ReactNode {
  const logo = resolveLogo(p.recipe)
  const c = logoColors(logo)
  return (
    <svg width={p.size} height={p.size} viewBox="0 0 100 100" role="img" aria-label="Corporation logo" data-testid={p.testId}>
      <rect x="2" y="2" width="96" height="96" rx="14" fill={c.field} />
      {logo.border ? <circle cx="50" cy="50" r="44" fill="none" stroke={c.accent} strokeWidth="4" /> : null}
      <path
        d={shapePath(logo.shape)}
        fill={c.emblem}
        stroke={c.accent}
        strokeWidth="2"
        transform={`rotate(${logo.rotation} 50 50)`}
        fillRule="evenodd"
      />
    </svg>
  )
}

export interface LogoEditorProps {
  recipe: LogoRecipe
  busy: boolean
  onChange(recipe: LogoRecipe): void
  /** First focus index in the `corp` group. */
  first: number
}

const STEPS: readonly { op: LogoOp; dir: 1 | -1; label: string }[] = [
  { op: 'shape', dir: -1, label: '◀ Shape' },
  { op: 'shape', dir: 1, label: 'Shape ▶' },
  { op: 'palette', dir: -1, label: '◀ Colours' },
  { op: 'palette', dir: 1, label: 'Colours ▶' },
  { op: 'rotate', dir: 1, label: 'Rotate' },
  { op: 'border', dir: 1, label: 'Ring' },
  { op: 'invert', dir: 1, label: 'Invert' },
]

/** How many focus indices the editor uses. */
export function logoEditorSize(): number {
  return logoKit().drafts + STEPS.length + 1
}

export function LogoEditor(p: LogoEditorProps): ReactNode {
  const drafts = seededDrafts(p.recipe.seed)
  const logo = resolveLogo(p.recipe)
  let i = p.first
  return (
    <div className="corp-logo" data-testid="corp-logo-editor">
      <div className="corp-logo__drafts">
        {drafts.map((d) => (
          <FocusButton
            key={d.draft}
            group="corp"
            index={i++}
            className={`ui-btn--small corp-logo__draft${d.draft === p.recipe.draft ? ' is-picked' : ''}`}
            testId={`corp-logo-draft-${d.draft}`}
            disabled={p.busy}
            onActivate={() => p.onChange(pickDraft(p.recipe.seed, d.draft))}
          >
            <LogoMark recipe={d} size={40} />
          </FocusButton>
        ))}
      </div>
      <div className="corp-logo__main">
        <LogoMark recipe={p.recipe} size={96} testId="corp-logo-preview" />
        <div className="corp-logo__steps">
          {STEPS.map((s) => (
            <FocusButton
              key={`${s.op}${s.dir}`}
              group="corp"
              index={i++}
              className="ui-btn--small"
              testId={`corp-logo-${s.op}${s.dir < 0 ? '-prev' : ''}`}
              disabled={p.busy}
              onActivate={() => p.onChange(applyStep(p.recipe, s.op, nextValue(logo, s.op, s.dir)))}
            >
              {s.label}
            </FocusButton>
          ))}
          <FocusButton
            group="corp"
            index={i++}
            className="ui-btn--small"
            testId="corp-logo-undo"
            disabled={p.busy || p.recipe.finishing.length === 0}
            onActivate={() => {
              const finishing = p.recipe.finishing.slice(0, -1)
              const r = resolveLogo({ ...p.recipe, finishing })
              p.onChange({
                ...p.recipe,
                finishing,
                shape: r.shape,
                palette: r.palette,
              })
            }}
          >
            Undo
          </FocusButton>
        </div>
      </div>
      <p className="fp-row__sub" data-testid="corp-logo-contribution">
        {humanContribution(p.recipe)}
      </p>
    </div>
  )
}
